import React, { useState, useEffect, Fragment } from 'react';
import { useDispatch, useSelector, batch } from 'react-redux';
import moment from 'moment';
import { ICONS } from 'Components/utils/icon/icons';
import { Button } from 'Components/frameworks/button/Button.jsx';
import { COLORS } from 'Components/utils/color/colors';
import {
  updateFilters,
  setSearchQuery,
  resetCriteria,
  changeKidsNumber,
  setKidsValidationState,
} from 'Store/faceting/facetingSearch.action';
import { searchQuerySelector, filtersSelector } from 'Store/faceting/faceting.selector';
import { setDepartureDate } from 'Store/trip-criteria/departure-date/departureDate.action';
import { setKeywords } from 'Store/search/query/searchQuery.action';
import { keywordsSelector } from 'Store/search/search.selector';
import {
  getDepartureDate,
  getTripDuration,
  getDepartureCity,
} from 'Store/trip-criteria/tripCriteria.selector';
import { DEFAULT_TRIP_DURATION } from 'Store/trip-criteria/duration/duration.constants';
import { setDepartureCity } from 'Store/trip-criteria/departure-city/departureCity.action';
import { getAdultsNumber, getKids } from 'Store/trip-information/storeHelper';
import { setAdultsNumber } from 'Store/trip-information/adults/adults.action';
import { setKids } from 'Store/trip-information/kids/kids.action';
import { t } from 'Src/i18n';
import {
  ACTIVITIES_FACET_ID,
  COUNTRIES_FACET_ID,
  DESTINATIONS_FACET_ID,
  TYPES_OF_STAY_FACET_ID,
  FACET_COMFORT_ID,
  DEPARTURE_CITY_FACET_ID,
} from 'Utils/faceting/constants';
import { useTracking } from 'Tracking/tracking-api/useTracking';
import { CriteriaControl } from './CriteriaControl';

export const CriteriaControls = () => {
  const searchQuery = useSelector(searchQuerySelector);
  const keywords = useSelector(keywordsSelector);
  const facettesFilters = useSelector(filtersSelector);
  const [searchQueryLabel, setSearchQueryLabel] = useState(searchQuery?.label);
  const [facettes, setFacettes] = useState(facettesFilters);
  const departureDate = useSelector(getDepartureDate);
  const [departureDateCriteria, setDepartureDateCriteria] = useState(departureDate);
  const tripDuration = useSelector(getTripDuration);
  const adultsNumber = useSelector(getAdultsNumber);
  const kids = useSelector(getKids);
  const [adultsNumberState, setAdultsNumberState] = useState(adultsNumber);
  const departureCityCode = useSelector(getDepartureCity);

  useEffect(
    () => {
      setFacettes(facettesFilters);
      setAdultsNumberState(adultsNumber);
    },
    [JSON.stringify(facettesFilters), adultsNumber],
  );

  const dispatch = useDispatch();

  let labelParts = '';

  if (adultsNumberState > 0) {
    const labelPartsTmp = [];
    const lengthPeople = adultsNumber + kids.length;
    if (lengthPeople) {
      if (adultsNumber >= 1) {
        labelPartsTmp.push(t('booking.form.selectAdultsLabels', { count: adultsNumber }));
      }
      if (kids.length >= 1) {
        labelPartsTmp.push(t('booking.form.selectChildrenLabels', { count: kids.length }));
      }
      labelParts = labelPartsTmp.join(' - ');
    }
  }

  const isFromAutoComplete = Boolean(searchQuery.type);

  const searchTerms = isFromAutoComplete || searchQuery.label ? searchQuery.label : keywords;

  const track = useTracking({
    categoryGA: 'search',
    actionGA: 'search',
    searchReferrer: 'search',
    searchTerms: searchTerms || '',
  });

  const removeDepartureDateCriteria = () => setDepartureDateCriteria(null);
  const removeFacetCriteria = ({ id, name }) =>
    setFacettes(
      [...facettes].filter(
        ({ id: facetteId, name: facetteName }) => id !== facetteId || name !== facetteName,
      ),
    );
  const clearSearchQuery = () => setSearchQueryLabel('');
  const submitFilters = () => {
    track({
      labelGA: 'noResult_applyCriterias',
    });
    batch(() => {
      dispatch(updateFilters(facettes));
      if (!departureDateCriteria) {
        dispatch(setDepartureDate(null));
      }
      if (searchQueryLabel !== searchQuery?.label) {
        if (keywords) {
          dispatch(setKeywords(''));
        }
        dispatch(setSearchQuery({ label: '' }));
      }
      const previousDepartureCity = facettesFilters.find(
        ({ id }) => id === DEPARTURE_CITY_FACET_ID,
      );
      const departureCity = facettes.find(({ id }) => id === DEPARTURE_CITY_FACET_ID);
      if (!departureCity && previousDepartureCity) {
        dispatch(setDepartureCity({ value: null }));
      }
      if (!adultsNumberState && adultsNumber) {
        dispatch(setAdultsNumber(0));
        dispatch(setKids([]));
        dispatch(changeKidsNumber(0));
        dispatch(setKidsValidationState([]));
      }
    });
  };
  const cleanCriteria = async () => {
    track({
      labelGA: 'noResult_resetCriterias',
    });
    batch(() => {
      if (adultsNumberState) {
        dispatch(setAdultsNumber(0));
        dispatch(setKids([]));
        dispatch(changeKidsNumber(0));
        dispatch(setKidsValidationState([]));
      }
      if (keywords) {
        dispatch(setKeywords(''));
      }
    });
    return dispatch(resetCriteria());
  };

  const clearParticipants = () => setAdultsNumberState(0);

  const noTransport = departureCityCode === 'NO';

  return (
    <Fragment>
      {searchQueryLabel && (
        <CriteriaControl onClear={clearSearchQuery} iconLeft={ICONS.SEARCH_GLASS}>
          {searchQueryLabel}
        </CriteriaControl>
      )}
      {departureDateCriteria && (
        <CriteriaControl
          onClear={removeDepartureDateCriteria}
          iconLeft={ICONS.DATE}
          id="departure_date"
        >{`${moment(departureDateCriteria).format('DD MMM')} - ${moment(departureDateCriteria)
          .add({ days: tripDuration || DEFAULT_TRIP_DURATION })
          .format('DD MMM')}`}</CriteriaControl>
      )}
      {!!labelParts && (
        <CriteriaControl
          onClear={clearParticipants}
          iconLeft={ICONS.USERS}
          key="participants"
          id="participants"
        >
          {labelParts}
        </CriteriaControl>
      )}
      {facettes.filter(({ id }) => id === DEPARTURE_CITY_FACET_ID).map(({ id, name, label }) => (
        <CriteriaControl
          onClear={() => removeFacetCriteria({ id, name })}
          iconLeft={noTransport ? ICONS.NO_TRANSPORT : ICONS.TRANSPORT}
          key={name}
          id={name}
        >
          {noTransport ? label : `${t('common.transport.from')} ${label}`}
        </CriteriaControl>
      ))}
      {facettes
        .filter(({ id }) => id === DESTINATIONS_FACET_ID || id === COUNTRIES_FACET_ID)
        .map(({ id, name }) => (
          <CriteriaControl
            iconLeft={ICONS.EARTH_PLANE}
            onClear={() => removeFacetCriteria({ id, name })}
            key={name}
            id={name}
          >
            {name}
          </CriteriaControl>
        ))}
      {facettes.filter(({ id }) => id === ACTIVITIES_FACET_ID).map(({ id, name }) => (
        <CriteriaControl
          iconLeft={ICONS.ACTIVITIES}
          onClear={() => removeFacetCriteria({ id, name })}
          key={name}
          id={name}
        >
          {name.split('|')[1]}
        </CriteriaControl>
      ))}
      {facettes.filter(({ id }) => id === TYPES_OF_STAY_FACET_ID).map(({ id, name }) => (
        <CriteriaControl
          key={name}
          id={name}
          iconLeft={ICONS.BEACH}
          onClear={() => removeFacetCriteria({ id, name })}
        >
          {t(`faceting.typesOfStay.label_${name.toLowerCase()}`)}
        </CriteriaControl>
      ))}
      {facettes.filter(({ id }) => id === FACET_COMFORT_ID).map(({ id, name }) => (
        <CriteriaControl
          key={name}
          id={name}
          iconLeft={ICONS.TRIDENT1}
          onClear={() => removeFacetCriteria({ id, name })}
        >
          {name}
        </CriteriaControl>
      ))}
      <div>
        <div className="inline-flex flex-col-reverse md:flex md:flex-row mt-4 justify-between">
          <button
            className="cursor-pointer text-gray-darker inline-flex flex items-center bg-transparent mt-4 md:mt-0"
            type="button"
            data-testid="reset-filters"
            onClick={cleanCriteria}
          >
            <span className="text-base underline">{t('common.comparator.resetCriteria')}</span>
          </button>
          <Button
            colorBg={COLORS.BOOKING}
            colorBorder={COLORS.BOOKING}
            onClick={submitFilters}
            dataTestId="apply-filters"
            size="medium"
          >
            {t('common.apply')}
          </Button>
        </div>
      </div>
    </Fragment>
  );
};
